import React from 'react';
import LegalLayout, { H2, P, Li, Value, LegalNote } from '../components/LegalLayout';
import { LEGAL } from '../data/legalConstants';

const DELETE_SUBJECT = 'ROMIND KIDS account deletion request';
const deletionMailto = `mailto:${LEGAL.privacyEmail}?subject=${encodeURIComponent(DELETE_SUBJECT)}`;

const DeleteAccount = () => (
  <LegalLayout title="Delete Your ROMIND KIDS Account" eyebrow="Account & Data Deletion" lastUpdated="October 21, 2026">
    <LegalNote title="Self-service account deletion" tone="safety">
      A parent or legal guardian can permanently delete the ROMIND KIDS family account directly inside the app. A support request is not required when the parent can access the account and the verified parent email address.
    </LegalNote>

    <H2>1. Where to Find Account Deletion</H2>
    <P>Withdrawing consent is not the same as submitting an account deletion request. Parents may manage or withdraw consent using the parent controls; withdrawing consent essential to a child account may restrict further access. To request erasure of account data, use the verified account deletion flow below or contact the privacy team if the in-app flow is unavailable. Some records may be lawfully retained for a limited purpose as described in the Data Retention &amp; Deletion Policy.</P>
    <P>Use this path inside the ROMIND KIDS app:</P>
    <P>
      <Value>ROMIND KIDS → Parent Controls → Delete Account</Value>
    </P>
    <ol className="list-decimal pl-5 mb-6 space-y-2 text-white/55 text-sm leading-relaxed">
      <li>Sign in to the ROMIND KIDS family account.</li>
      <li>Open <Value>Parent Controls</Value>.</li>
      <li>Enter the parent password.</li>
      <li>Enter the fresh six-digit verification code sent to the verified parent email address.</li>
      <li>Scroll to the bottom of Parent Controls and select <Value>Delete Account</Value>.</li>
      <li>Read the warning explaining what will be permanently deleted.</li>
      <li>Request a separate new six-digit deletion code.</li>
      <li>Enter the new deletion code sent to the verified parent email address.</li>
      <li>Confirm <Value>Permanently Delete Account</Value>.</li>
    </ol>

    <LegalNote title="Two separate six-digit codes" tone="neutral">
      Access to Parent Controls requires the parent password and a fresh six-digit code sent to the parent email. Final account deletion requires a separate new six-digit code sent to the same verified email address.
    </LegalNote>

    <H2>2. What Is Deleted</H2>
    <P>After final confirmation, ROMIND KIDS closes the family account and deletes or de-identifies the associated user data, including:</P>
    <ul>
      <Li>The parent account and all linked child profiles.</Li>
      <Li>Profile details, avatars, preferences, interests, and parent-control settings.</Li>
      <Li>Posts, comments, messages, uploaded media, friendships, and social activity.</Li>
      <Li>Academy progress, club activity, challenges, achievements, rewards, and portfolio records.</Li>
      <Li>Active sessions, authentication tokens, device registrations, and push-notification tokens.</Li>
    </ul>

    <LegalNote title="Permanent action" tone="warning">
      Account deletion cannot be undone. Deleted profiles, messages, creative work, progress, achievements, and rewards may not be recoverable.
    </LegalNote>

    <H2>3. Limited Records That May Be Retained</H2>
    <P>ROMIND KIDS may retain only limited records where required for child safety, security, fraud prevention, legal compliance, accounting, payment disputes, or enforcement. Such records are restricted to those purposes and are not kept as an active user account.</P>

    <H2>4. Google Play Subscription</H2>
    <P>Deleting a ROMIND KIDS account does not automatically cancel an active Google Play subscription. To prevent future renewal charges, cancel the subscription separately in Google Play under Payments &amp; subscriptions → Subscriptions.</P>

    <H2>5. If the In-App Option Cannot Be Used</H2>
    <P>If the parent cannot sign in, cannot access the verified email address, or the in-app deletion function fails because of a technical problem, send an account-deletion request to:</P>
    <P>
      <Value>
        <a href={deletionMailto} className="text-cyan-300 hover:text-cyan-200 transition-colors">
          {LEGAL.privacyEmail}
        </a>
      </Value>
    </P>
    <P>Do not send a password or any six-digit verification code by email.</P>
  </LegalLayout>
);

export default DeleteAccount;
